import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from './useAuth';
import { MEMBERSHIPS_KEY } from './useClinics';
import type {
  PlatformClinic,
  PlatformClinicMember,
  PlatformClinicsPage,
  PlatformUser,
} from '@/types/api';

/**
 * Queries for the platform dashboard.
 *
 * Every call goes through `api` (absolute paths) rather than `clinicApi`: these
 * routes live above the tenant boundary and name no clinic, and `clinicApi`
 * would throw outside a tenant namespace — which `/admin` always is.
 *
 * The query keys are namespaced under `admin` so nothing here shares a cache with
 * a clinic's own data. There is no slug in any key for the same reason.
 */
export const ADMIN_CLINICS_KEY = ['admin', 'clinics'] as const;
export const ADMIN_USERS_KEY = ['admin', 'users'] as const;
export const ADMIN_SUPER_ADMINS_KEY = ['admin', 'super-admins'] as const;

/** Matches `searchUsersQuery`'s server-side floor, so a short query never leaves. */
export const USER_SEARCH_MIN_CHARS = 3;

export function usePlatformClinics(opts: { query: string; limit: number; offset: number }) {
  const { isAuthenticated } = useAuth();
  const params = new URLSearchParams({
    limit: String(opts.limit),
    offset: String(opts.offset),
  });
  if (opts.query) params.set('query', opts.query);

  return useQuery({
    queryKey: [...ADMIN_CLINICS_KEY, 'list', opts.query, opts.limit, opts.offset],
    queryFn: () => api.get<PlatformClinicsPage>(`/api/admin/clinics?${params.toString()}`),
    enabled: isAuthenticated,
  });
}

/**
 * One clinic and its members, for the detail page.
 *
 * Two queries rather than one endpoint returning both: the header re-renders on
 * every toggle while the member list does not change, and the toggle already
 * invalidates `ADMIN_CLINICS_KEY` — which this shares a prefix with, so the
 * header refreshes and the list is left alone.
 */
export function usePlatformClinic(clinicId: string | undefined) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: [...ADMIN_CLINICS_KEY, 'detail', clinicId],
    queryFn: () => api.get<PlatformClinic>(`/api/admin/clinics/${clinicId}`),
    enabled: isAuthenticated && Boolean(clinicId),
  });
}

export function usePlatformClinicMembers(clinicId: string | undefined) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: [...ADMIN_CLINICS_KEY, 'members', clinicId],
    queryFn: () => api.get<PlatformClinicMember[]>(`/api/admin/clinics/${clinicId}/members`),
    enabled: isAuthenticated && Boolean(clinicId),
  });
}

/**
 * Both directions of the clinic toggle. One hook, because they invalidate the
 * same list and differ only in the verb — the SEPARATION that matters is on the
 * server, where they are two endpoints with two audit actions.
 */
export function useSetClinicDisabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, disabled }: { id: string; disabled: boolean }) =>
      api.post<unknown>(`/api/admin/clinics/${id}/${disabled ? 'disable' : 'enable'}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_CLINICS_KEY });
      // The operator may be a member of the clinic they just switched off. Their
      // own membership list carries `disabled`, and a stale copy would keep
      // offering it as somewhere to go — straight into a 403.
      void qc.invalidateQueries({ queryKey: MEMBERSHIPS_KEY });
    },
  });
}

/**
 * User search. Below the minimum length the query does not fire at all, so the
 * UI shows its own hint rather than a 400 the operator has to interpret.
 */
export function usePlatformUserSearch(query: string) {
  const { isAuthenticated } = useAuth();
  const trimmed = query.trim();
  return useQuery({
    queryKey: [...ADMIN_USERS_KEY, 'search', trimmed],
    queryFn: () =>
      api.get<PlatformUser[]>(`/api/admin/users?query=${encodeURIComponent(trimmed)}`),
    enabled: isAuthenticated && trimmed.length >= USER_SEARCH_MIN_CHARS,
  });
}

/**
 * Set a user's password.
 *
 * Nothing is invalidated on success: the password is not part of any cached view,
 * and deliberately never enters one — it must not reach a query cache, a URL, or
 * an error message.
 */
export function useSetUserPassword() {
  return useMutation({
    mutationFn: ({ userId, newPassword }: { userId: string; newPassword: string }) =>
      api.post<{ reset: true }>(`/api/admin/users/${userId}/password`, { newPassword }),
  });
}

export function useSuperAdmins() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: [...ADMIN_SUPER_ADMINS_KEY, 'list'],
    queryFn: () => api.get<PlatformUser[]>('/api/admin/super-admins'),
    enabled: isAuthenticated,
  });
}

/**
 * Promote and demote both invalidate the super-admin list AND any user search
 * on screen, because a search row renders `isSuperAdmin` and would otherwise
 * keep offering "Promote" for someone who now is one.
 */
function useSuperAdminMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_SUPER_ADMINS_KEY });
      void qc.invalidateQueries({ queryKey: ADMIN_USERS_KEY });
    },
  });
}

export function useGrantSuperAdmin() {
  return useSuperAdminMutation((userId: string) =>
    api.post<PlatformUser>('/api/admin/super-admins', { userId }),
  );
}

export function useRevokeSuperAdmin() {
  return useSuperAdminMutation((userId: string) =>
    api.delete<{ revoked: true }>(`/api/admin/super-admins/${userId}`),
  );
}
